import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../api/apiClient';
import { GET_SONG_VERSIONS_URL } from '../../../api/song';
import EmptyState from '../../../components/shared/EmptyState';
import LabelAvatarStack from '../../../components/shared/LabelAvatarStack';
import SharedHeader from '../../../components/shared/SharedHeader';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import { toast } from '../../../components/shared/toast';
import en from '../../../translations/en.json';

// Deterministic pastel color palette for owner avatar stacks
const AVATAR_COLORS = ['#fca5a5', '#c7d2fe', '#fed7aa', '#86efac', '#a5f3fc', '#f9a8d4'];

function VersionsTab({ song }) {
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  useEffect(() => {
    if (!song?.id) return;

    const fetchVersions = async () => {
      setIsLoading(true);
      try {
        const data = await apiClient(GET_SONG_VERSIONS_URL(song.id));
        if (data && data.versions) {
          setVersions(data.versions);
        }
      } catch (err) {
        console.warn('Failed to load song versions:', err);
        setVersions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVersions();
  }, [song?.id]);

  const handleExportVersions = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      const data = await apiClient(GET_SONG_VERSIONS_URL(song.id));
      if (data && data.versions) {
        const headers = ['Version Name', 'Status', 'Artist', 'Lyrics', 'Music', 'Ownership', 'Notes', 'Conflicts'];
        const rows = data.versions.map(v => [
          v.versionName,
          v.status,
          v.artist,
          v.lyrics,
          v.music,
          `${v.ownership}%`,
          v.notes,
          v.conflicts || v.conflict || 'No'
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        const formattedName = (song?.name || 'song').replace(/\s+/g, '_');
        link.setAttribute('download', `song_versions_${formattedName}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Song versions exported successfully!');
      } else {
        toast.error('Failed to export versions.');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export versions list.');
    } finally {
      setIsExporting(false);
    }
  };

  const filteredVersions = versions.filter(v => 
    !searchQuery.trim() || 
    (v.versionName && v.versionName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (v.artist && v.artist.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // ── Loading skeleton rows ─────────────────────────────────────────────────
  const SkeletonRow = () => (
    <tr>
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <td key={i}>
          <div
            style={{
              height: 12,
              background: '#e2e8f0',
              borderRadius: 4,
              width: i === 1 ? '80%' : i === 3 || i === 4 ? '70%' : '50%',
            }}
          />
        </td>
      ))}
    </tr>
  );

  // ── Empty state: Version song OR no versions linked ───────────────────────
  const isVersionSong = song?.versionType === 'Version';
  const showEmpty = !isLoading && (isVersionSong || filteredVersions.length === 0);

  const emptyTitle = isVersionSong
    ? 'Version songs have no sub-versions'
    : 'No version songs found';
  const emptySubtitle = isVersionSong
    ? 'Only original songs can have version songs linked to them.'
    : 'No version songs are linked to this original song yet.';

  return (
    <div className="versions-tab-container">
      <LoadingOverlay isLoading={isExporting} message={t('common.exportingMessage') || 'Exporting data...'} />
      <SharedHeader
        title=""
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('songs.searchPlaceholder') || 'Search versions...'}
        resultCount={versions.length}
        resultLabel="Versions"
        showSortFilter={false}
        onExportClick={handleExportVersions}
      />
      {/* Table responsive container */}
      <div className="versions-table-wrapper" style={{ marginTop: '16px' }}>
        {showEmpty ? (
          // Show shared EmptyState — preserves the existing layout section
          <EmptyState title={emptyTitle} subtitle={emptySubtitle} hasSearch={false} />
        ) : (
          <table className="versions-table">
            <thead>
              <tr>
                <th>Version Name</th>
                <th>Status</th>
                <th>Artist</th>
                <th>Lyrics &amp; Music</th>
                <th>Owners</th>
                <th>Ownership %</th>
                <th>Notes / Cases</th>
                <th>Conflicts</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                // Skeleton rows while loading
                [1, 2, 3].map((i) => <SkeletonRow key={i} />)
              ) : (
                filteredVersions.map((row) => (
                  <tr key={row.id}>
                    {/* 1. Version Name */}
                    <td>
                      <div className="tbl-version-name">
                        <span className="bold-name">{row.versionName}</span>
                        <span className="sub-name">{row.parentSong}</span>
                      </div>
                    </td>

                    {/* 2. Status */}
                    <td>
                      <span className="tbl-status-badge">
                        <span className="green-dot" />
                        {row.status}
                      </span>
                    </td>

                    {/* 3. Artist */}
                    <td>
                      <div className="tbl-artist-info">
                        <span className="bold-artist">{row.artist}</span>
                      </div>
                    </td>

                    {/* 4. Lyrics & Music */}
                    <td>
                      <div className="tbl-lyrics-music">
                        <span className="bold-lm">{row.lyrics}</span>
                        <span className="sub-lm">{row.music}</span>
                      </div>
                    </td>

                    {/* 5. Labels */}
                    <td>
                      <LabelAvatarStack labels={row.labels || row.recordLabels} />
                    </td>

                    {/* 6. Ownership % */}
                    <td>
                      <span className="tbl-ownership-pct">{row.ownership}%</span>
                    </td>

                    {/* 7. Notes / Cases */}
                    <td>
                      <span className="tbl-notes">{row.notes}</span>
                    </td>

                    {/* 8. Conflicts */}
                    <td>
                      <span className="tbl-conflicts">{row.conflicts || row.conflict || 'No'}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default VersionsTab;
